import { useContext, useEffect, useRef, useState } from "react";
import useCurrentGame from "../../hooks/useCurrentGame";
import INTOUCH_API from "../../services/Api";
import Game from "../../store/Game";
import Swiper from "../../store/Swiper";
import useModalActions from "../../hooks/useModalActions";
import { MODAL_TYPES } from "../../utils/emitter";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import useAuth from "../../hooks/useAuth";
import {
  COUNT_CARDS,
  CURRENT_TEMP_DEMO_GENDER,
  TEMPORARY_ID_KEY,
  GENDER_CHANGED_AT_KEY,
} from "../../utils/constants";
import useBrowserStorage from "../../hooks/useBrowserStorage";
import { SOCKET_EVENTS, SocketContext } from "../../components/SocketProvider";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../utils/routes";
import { autorun } from "mobx";
import "./style.scss";
import PersonalAccount from "../../store/PersonalAccount";
import trackEvent from "../../utils/analytics";

const typesAnswers = {
  positive: 1,
  neutral: 2,
  negative: 3,
};

export default function useViewController(onFeedbackSwipe) {
  const { sendEvent } = useContext(SocketContext);
  const { getCurrentGame } = useCurrentGame();
  const { openModal, closeModal } = useModalActions();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { getItem, setItem } = useBrowserStorage();
  const { getUserData } = useCurrentUserData();
  const location = useLocation();

  // modal mobile controller
  const [isOpen, setIsOpen] = useState(false);
  // for display the current card
  const [countCard, setCountCard] = useState(0);
  const countCardRef = useRef(0); // Актуальное значение для использования в callbacks
  // GA4: последний отправленный рубеж card_reached (дедуп повторных достижений)
  const maxTrackedCardRef = useRef(0);
  // for display progress the current card
  const [progressCountCard, setProgressCountCard] = useState(0);

  const [completedCards, setCompletedCards] = useState([]);

  // to disable buttons when swiping
  const [isSwiping, setIsSwiping] = useState(false);
  const [isAnswerAllLoading, setIsAnswerAllLoading] = useState(false);

  // checkEndGame перенесен в controller.js и вызывается после onFeedbackSwipe

  const incrementProgressCountCard = () => {
    setProgressCountCard((prev) => prev + 1);
  };
  const incrementCountCard = () => {
    countCardRef.current += 1; // Синхронно обновляем ref
    setCountCard((prev) => prev + 1);
    // GA4 «Stats v2»: каждая 5-я показанная карточка — чтобы видеть, где интерес
    // пропадает (card_number = номер карты на экране, счёт с 1). Шлём только при
    // ПЕРВОМ достижении рубежа: листание назад-вперёд по отвеченным картам и
    // восстановленный прогресс не должны дублировать события.
    const shownCardNumber = countCardRef.current + 1;
    if (shownCardNumber % 5 === 0 && shownCardNumber > maxTrackedCardRef.current) {
      maxTrackedCardRef.current = shownCardNumber;
      trackEvent("card_reached", { card_number: shownCardNumber });
    }
  };
  const decrementCountCard = () => {
    if (countCardRef.current === 0) return;
    countCardRef.current -= 1; // Синхронно обновляем ref
    setCountCard((prevState) => prevState - 1);
  };
  const setCompleteCards = (card) => {
    const answeredCard = completedCards.find(
      (completedCard) =>
        (completedCard?.questionId || completedCard?.id) === card?.id
    );
    if (answeredCard) {
      updateCard(answeredCard, card);
      return answeredCard;
    }
    setCompletedCards((prevState) => {
      incrementProgressCountCard();
      prevState[countCard] = card;
      return [...prevState];
    });
  };
  const updateCard = (answeredCard, card) => {
    const updatedCards = completedCards.map((completedCard) => {
      const isTheSameCard =
        (completedCard?.questionId || completedCard?.id) ===
        (answeredCard?.questionId || answeredCard?.id);
      if (isTheSameCard) {
        if (Swiper.isPositive || Swiper.isToRight) {
          completedCard = { ...answeredCard, answer: 1 };
        }
        if (Swiper.isNegative || Swiper.isToLeft) {
          completedCard = { ...answeredCard, answer: 3 };
        }
        if (Swiper.isNeutral || Swiper.isToTop) {
          completedCard = { ...answeredCard, answer: 2 };
        }
      }
      return completedCard;
    });
    return setCompletedCards(updatedCards);
  };

  const isAnsweredCardsExist = (swipeCard) => {
    if (!completedCards.length) return;
    const currentCard = completedCards.find((card) => {
      return swipeCard?.id === (card?.questionId || card?.id);
    });
    return !currentCard;
  };
  const calculatePercentProgress = () => {
    if (!completedCards.length) return 0;
    const answeredCards = completedCards.filter((card) => card?.answer > 0);
    return (answeredCards.length / completedCards.length) * 100;
  };

  const SWIPE_ANIMATION_DURATION = 300; // ms - синхронизировано с CSS transition

  const continueSwipe = (card, stepX, stepY, endSwipe) => {
    if (Swiper.isContinueSwiping) return;
    Swiper.setIsContinueSwiping(true);

    // Убираем класс is-swiping чтобы CSS transition заработал
    setIsSwiping(false);

    // Вычисляем конечную позицию
    let targetX, targetY, targetRotate;

    if (!stepX) {
      // Свайп вверх (neutral)
      targetX = 0;
      targetY = -500;
      targetRotate = 0;
    } else if (stepX > 0) {
      // Свайп вправо (positive)
      targetX = 350;
      targetY = 0;
      targetRotate = 25;
    } else {
      // Свайп влево (negative)
      targetX = -350;
      targetY = 0;
      targetRotate = -25;
    }

    // Устанавливаем конечную позицию — CSS transition сделает плавную анимацию
    Swiper.setTransform({
      x: targetX,
      y: targetY,
      rotate: targetRotate,
    });

    // Ждём окончания CSS transition
    setTimeout(() => {
      sendAnswerBySwipe(card);
      endSwipe();
      preventCardState();
    }, SWIPE_ANIMATION_DURATION);
  };

  const preventCardState = () => {
    Swiper.setTransform({ x: 0, y: 0, rotate: 0 });
    Swiper.setOpacityCard(1);
    Swiper.setIsContinueSwiping(false);
  };

  // Получаем актуальную карточку по ref (не зависит от рендер-цикла React)
  const getCurrentCard = () => Game.cards[countCardRef.current];

  const onSwipeEnd = (event) => {
    // Блокируем новый свайп пока анимация предыдущего не завершена
    if (Swiper.isContinueSwiping) {
      return;
    }

    // Берём актуальную карточку из ref, а не из параметра
    const card = getCurrentCard();
    if (!card) return;

    if (Swiper.isToLeft) {
      continueSwipe(card, -5, -5, () => {
        Swiper.setIsToLeft(false);
      });
    }
    if (Swiper.isToRight) {
      continueSwipe(card, 5, 5, () => {
        Swiper.setIsToRight(false);
      });
    }
    if (Swiper.isToTop) {
      continueSwipe(card, null, -5, () => {
        Swiper.setIsToTop(false);
      });
    }

    setIsSwiping(false);
    if (!Swiper.isToTop && !Swiper.isToLeft && !Swiper.isToRight) {
      preventCardState();
    }
  };

  const positiveSwipe = () => {
    if (Swiper.isPositive || Swiper.isContinueSwiping) return;
    const card = getCurrentCard();
    if (!card) return;
    Swiper.setIsPositive(true);
    const answeredCard = { answer: typesAnswers.positive, id: card.id };
    setIsSwiping(true);
    onFeedbackSwipe(answeredCard);
    setCompleteCards(answeredCard);
    setTimeout(() => {
      Swiper.setIsPositive(false);
      setIsSwiping(false);
      incrementCountCard();
    }, 600);
  };

  const negativeSwipe = () => {
    if (Swiper.isNegative || Swiper.isContinueSwiping) return;
    const card = getCurrentCard();
    if (!card) return;
    Swiper.setIsNegative(true);
    const answeredCard = { answer: typesAnswers.negative, id: card.id };
    setIsSwiping(true);
    onFeedbackSwipe(answeredCard);
    setCompleteCards(answeredCard);
    setTimeout(() => {
      Swiper.setIsNegative(false);
      setIsSwiping(false);
      incrementCountCard();
    }, 600);
  };

  const neutralSwipe = () => {
    if (Swiper.isNeutral || Swiper.isContinueSwiping) return;
    const card = getCurrentCard();
    if (!card) return;
    Swiper.setIsNeutral(true);
    const answeredCard = { answer: typesAnswers.neutral, id: card.id };
    setIsSwiping(true);
    onFeedbackSwipe(answeredCard);
    setCompleteCards(answeredCard);
    setTimeout(() => {
      Swiper.setIsNeutral(false);
      setIsSwiping(false);
      incrementCountCard();
    }, 600);
  };

  const forwardSwipe = () => {
    if (countCard === Game.cards.length) return;
    if (!completedCards.length) return;
    setIsSwiping(true);
    incrementCountCard();
    setIsSwiping(false);
  };
  const backSwipe = () => {
    if (countCard === 0) return;
    setIsSwiping(true);
    decrementCountCard();
    setIsSwiping(false);
  };
  const sendAnswerBySwipe = (card) => {
    const answeredCard = getAnswer(card);
    if (!answeredCard) return;
    onFeedbackSwipe(answeredCard);
    setCompleteCards(answeredCard);
    // Переход к следующей карточке
    if (Swiper.isToRight || Swiper.isToLeft || Swiper.isToTop) {
      incrementCountCard();
    }
  };
  const getAnswer = (card) => {
    if (Swiper.isToRight) {
      return { answer: typesAnswers.positive, id: card.id };
    }
    if (Swiper.isToLeft) {
      return { answer: typesAnswers.negative, id: card.id };
    }
    if (Swiper.isToTop) {
      return { answer: typesAnswers.neutral, id: card.id };
    }
  };

  const onSwipeMove = (position, event) => {
    // prevent touch swipe if swiping by button
    if (Swiper.isNeutral || Swiper.isNegative || Swiper.isPositive) return;
    if (Swiper.isContinueSwiping) return;
    setIsSwiping(true);
    // for top swipe
    if (position.y < -100) {
      if (Swiper.isToRight || Swiper.isToLeft) {
        Swiper.setTransform({ ...Swiper.transform, y: position.y });
        return;
      }
      Swiper.setIsToTop(true);
    } else {
      Swiper.setIsToTop(false);
    }
    // for right swipe
    if (position.x > 70) {
      if (Swiper.isToTop) return;
      Swiper.setIsToRight(true);
    } else {
      Swiper.setIsToRight(false);
    }
    // for left swipe
    if (position.x < -70) {
      if (Swiper.isToTop) return;
      Swiper.setIsToLeft(true);
    } else {
      Swiper.setIsToLeft(false);
    }
    // for top swipe
    if (position.y < -15 && (position.x > -30 || position.x < 30)) {
      Swiper.setTransform({ ...Swiper.transform, y: position.y });
      return;
    }

    // for left-right swipe
    if (
      position.x <= 250 &&
      position.x > -250 &&
      position.y <= 50 &&
      position.y > -50
    ) {
      Swiper.setTransform({
        x: calculateMovement(position, "x"),
        y: calculateMovement(position, "y"),
        rotate: `${calculateRotate(position, "x")}`,
      });
    }
  };
  const opacityController = () => {
    const transform = Swiper.transform;
    if (transform.y < -20 && transform.y > -50) {
      Swiper.setOpacityCard(0.7);
    } else if (transform.y < -51 && transform.y > -100) {
      Swiper.setOpacityCard(0.5);
    } else if (transform.y < -101) {
      Swiper.setOpacityCard(0.1);
    }
    if (
      (transform.rotate >= 3 && transform.rotate <= 7) ||
      (transform.rotate <= -3 && transform.rotate >= -7)
    ) {
      Swiper.setOpacityCard(0.7);
    } else if (
      (transform.rotate >= 8 && transform.rotate <= 14) ||
      (transform.rotate <= -8 && transform.rotate >= -14)
    ) {
      Swiper.setOpacityCard(0.5);
    } else if (
      (transform.rotate >= 15 && transform.rotate <= 17) ||
      (transform.rotate <= -15 && transform.rotate >= -17)
    ) {
      Swiper.setOpacityCard(0.3);
    } else if (
      (transform.rotate >= 18 && transform.rotate <= 20) ||
      (transform.rotate <= -18 && transform.rotate >= -20)
    ) {
      Swiper.setOpacityCard(0.1);
    }
  };

  const calculateMovement = (position, axis) => {
    return position[axis] * Math.cos(19);
  };
  const calculateRotate = (position, axis) => {
    return position[axis] / 12;
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const resetGameProgress = async () => {
    const userData = getUserData();
    const form = {
      gameId: Game.game.id,
    };

    if (token) {
      form.userId = userData.id;
    } else {
      form.temporaryUserId = getItem(TEMPORARY_ID_KEY);
    }
    await INTOUCH_API.requestManager(
      "resetGameAnswersProgress",
      form,
      () => {
        Game.setGameLastAnswers([]);
        Game.setGameHistory([]);
        setProgressCountCard(0);
        setCountCard(0);
        countCardRef.current = 0;
      },
      () => {}
    );
  };

  // Проверяем, не закончена ли игра по истории ответов
  const checkEndGameByHistory = () => {
    const totalCards = Game.cards.length;
    const history = Game.gameHistory || [];
    if (!totalCards || !history.length) return;

    // считаем отвеченными только карты ТЕКУЩЕЙ колоды: gameHistory содержит ответы
    // всех наборов, и без фильтра 88 базовых мгновенно «завершали» набор +57 (88>=57)
    const deckIds = new Set(Game.cards.map((card) => card.id));
    // item 2: завершение считаем по РАЗНЫМ questionId (как гейт бэка), а не по длине
    // списка — иначе дубли/DELETED-ответы «завершали» игру при реальной дыре в наборе
    const answeredDistinct = new Set(
      history
        .filter((card) => card.answer > 0 && deckIds.has(card.questionId))
        .map((card) => card.questionId)
    ).size;
    if (answeredDistinct >= totalCards) {
      const user = getUserData();
      if (user && user.id) {
        const game = getCurrentGame();

        sendEvent(SOCKET_EVENTS.END_GAME, {
          senderId: user.id,
          room: game.id,
          isEnd: true,
        });
      }
      // сюда попадаем и после «использовать ответы прошлой игры» (copyGameAnswer):
      // продукт шлёт END_GAME — это реальное завершение, GA-событие тоже нужно
      navigate(ROUTES.END_GAME_PAGE, {
        state: {
          justCompleted: true,
          gameId: getCurrentGame()?.id,
          setType: "BASE",
        },
      });
    }
  };

  const onUsePrevGameAnswer = async () => {
    const game = getCurrentGame();
    const temporaryUserId = getItem(TEMPORARY_ID_KEY);
    const currentUser = getUserData();
    const data = {
      gameId: game.id,
    };
    if (token) {
      data.userId = currentUser.id;
    } else {
      data.temporaryUserId = temporaryUserId;
    }

    await INTOUCH_API.requestManager(
      "copyGameAnswer",
      data,
      async (response) => {
        if (response.data) {
          await Game.getGameHistory(data);
          closeModal(MODAL_TYPES.USING_PREV_GAME_ANSWER);
          await checkEndGameByHistory();
        } else {
          setProgressCountCard(0);
          setCountCard(0);
        }
      },
      () => {}
    );
  };

  // item 3: есть ли среди прошлых ответов такие, что даны УЖЕ в текущем поле.
  // Без отметки о смене пола (её нет / другое устройство) ведём себя как раньше.
  const hasPrevAnswersFromCurrentGender = () => {
    const answers = Game.lastGameAnswers || [];
    if (!answers.length) return false;
    let changedAt = null;
    try {
      changedAt = localStorage.getItem(GENDER_CHANGED_AT_KEY);
    } catch (e) {
      changedAt = null;
    }
    if (!changedAt) return true;
    const changedTs = Date.parse(changedAt);
    if (Number.isNaN(changedTs)) return true;
    return answers.some((a) => {
      const ts = Date.parse(a?.dateAnswer);
      // без даты считаем ответ «старым» — безопаснее не переносить
      return !Number.isNaN(ts) && ts >= changedTs;
    });
  };

  const onRestoreGameHistory = async ({
    isCopyAnswer = false,
    isRestore = false,
  } = {}) => {
    const game = getCurrentGame();
    if (!game) return;
    // item 3d: этот метод дёргается из autorun, который срабатывает и на маунте — когда
    // колода ещё грузится (controller.getCards → Game.getGameHistory). При пустой
    // Game.cards deckIds пуст → firstGapIndex=-1 → resumeIndex=0, и «продолжить с места»
    // вставало в НАЧАЛО; после загрузки autorun перезапускался и индекс «перепрыгивал».
    // Ждём колоду — расчёт resumeIndex ниже уже корректен.
    if (!Game.cards.length) return;
    setCompletedCards(new Array(Game.cards.length));
    const temporaryUserId = getItem(TEMPORARY_ID_KEY);
    const currentUser = getUserData();
    const data = {
      gameId: game.id,
    };
    if (token) {
      data.userId = currentUser?.id;
    } else {
      data.temporaryUserId = temporaryUserId;
    }
    const gameHistory = Game.gameHistory;
    // прогресс восстанавливаем только по картам ТЕКУЩЕЙ колоды — иначе на игре с
    // пройденной базой (88 ответов) свежекупленный набор +57 открывался сразу
    // «57/57» без единой карты (countCard=88 > 57). Фильтр по deckIds, как в
    // controller.checkEndGameOnInit.
    const deckIds = new Set(Game.cards.map((card) => card.id));
    if (gameHistory.length) {
      const answeredCards = gameHistory.filter(
        (card) => card.answer > 0 && deckIds.has(card.questionId)
      );
      // item 2 (баг «87/88, продолжить тест» по кругу): считаем отвеченные по РАЗНЫМ
      // questionId (как гейт бэка), а возобновляемся с ПЕРВОЙ неотвеченной карты
      // колоды — а НЕ по длине списка ответов. Раньше при потере одного ответа в
      // середине countCard=answeredCards.length вставал на ПОСЛЕДНЮЮ (уже отвеченную)
      // карту: юзер пере-раскидывал последние карты, дыра в середине не закрывалась,
      // distinct оставался 87 → бэк «ждём партнёра» → цикл. Теперь возобновление
      // ведёт ровно на пропущенную карту и закрывает дыру.
      const answeredIds = new Set(answeredCards.map((card) => card.questionId));
      const answeredCount = answeredIds.size;
      const firstGapIndex = Game.cards.findIndex(
        (card) => !answeredIds.has(card.id)
      );
      const resumeIndex =
        firstGapIndex === -1 ? Game.cards.length : firstGapIndex;
      if (
        Game.cards.length &&
        answeredCount > 0 &&
        answeredCount !== Game.cards.length
      ) {
        openModal(MODAL_TYPES.RESTORE_DEMO_PROGRESS);
      }
      setProgressCountCard(answeredCount);
      setCountCard(resumeIndex);
      countCardRef.current = resumeIndex;
      // восстановленный прогресс не считается «достижением» — рубежи до него не шлём
      maxTrackedCardRef.current = Math.max(resumeIndex, answeredCount) + 1;
      // item 2: раскладываем ответы по ПОЗИЦИЯМ колоды (а не «пакуем» в начало).
      // При возобновлении с дыры в середине упаковка в начало съезжала: подсветка
      // ±-кнопок (prevAnswer=completedCards[countCard]) и прогресс-бар показывали
      // неверную карту. Теперь индекс completedCards совпадает с индексом колоды.
      const answerByQid = new Map(
        answeredCards.map((a) => [a.questionId, a.answer])
      );
      setCompletedCards(() =>
        Game.cards.map((card) =>
          answerByQid.has(card.id)
            ? { answer: answerByQid.get(card.id), id: card.id }
            : undefined
        )
      );
    } else if (
      // item 3: не предлагаем переносить ответы, данные ДО смены пола. Тексты карточек
      // гендерные и взаимно обратные (Questions.f_ru ↔ m_ru), а пол ответа нигде не
      // хранится — перенос «женских» ответов в мужскую роль даёт зеркальный смысл.
      // Отсекаем по Answers.dateAnswer относительно момента смены пола.
      hasPrevAnswersFromCurrentGender() &&
      PersonalAccount.games.length > 1 &&
      !PersonalAccount.hasUsedPrevAnswersPopup(game.id)
    ) {
      await PersonalAccount.addGameIdsByUsingPrevAnswersPopup(game.id);
      openModal(MODAL_TYPES.USING_PREV_GAME_ANSWER);
    }
  };

  useEffect(() => {
    if (token) return;
    // Гендер-попап показываем строго один раз — в самом начале демо.
    // Любой признак того, что юзер уже играл/выбирал пол ранее — повод
    // не показывать модалку, даже если localStorage сбросился (Safari ITP,
    // инкогнито, другой браузер). См. WEPURPLAY_TODO § Q2.
    const alreadyKnown =
      getItem(CURRENT_TEMP_DEMO_GENDER) ||
      PersonalAccount.user?.gender ||
      (Game.gameHistory && Game.gameHistory.length > 0);
    if (alreadyKnown) {
      // Safety-net: гендер-модалка могла открыться на первом рендере, когда
      // gameHistory ещё не подгрузился. Если позже выяснилось, что
      // прогресс/пол уже есть — закрываем её, чтобы не висела поверх
      // «продолжить с места».
      closeModal(MODAL_TYPES.CHOOSE_YOUR_GENDER);
      return;
    }
    openModal(MODAL_TYPES.CHOOSE_YOUR_GENDER);
  }, [token, Game.gameHistory.length, PersonalAccount.user?.gender]);

  // Перенесли проверку конца игры в onFeedbackSwipe в controller.js

  useEffect(() => {
    if (countCard + 1 === 15) {
      const game = getCurrentGame();
      if (!game) return;
      const wantToKnowGameId = getItem(MODAL_TYPES.WANT_TO_KNOW);
      if (wantToKnowGameId === game.id) {
        return;
      }
      if (game.type === "DEMO" && location.state?.from === "landing") {
        openModal(MODAL_TYPES.WANT_TO_KNOW);
        setItem(MODAL_TYPES.WANT_TO_KNOW, game.id);
      }
    }
  }, [countCard, location.state?.from]);

  useEffect(
    () =>
      autorun(() => {
        opacityController();
      }),
    []
  );

  useEffect(
    () =>
      autorun(() => {
        onRestoreGameHistory();
      }),
    []
  );

  return {
    incrementCountCard,
    decrementCountCard,
    countCard,
    onSwipeEnd,
    onSwipeMove,
    isToRight: Swiper.isToRight,
    isToLeft: Swiper.isToLeft,
    isToTop: Swiper.isToTop,
    opacityCard: Swiper.opacityCard,
    completedCards,
    isPositive: Swiper.isPositive,
    isNeutral: Swiper.isNeutral,
    isNegative: Swiper.isNegative,
    isSwiping,
    progressCountCard,
    isOpen,
    toggleModal,
    isAnsweredCardsExist,
    positiveSwipe,
    negativeSwipe,
    neutralSwipe,
    forwardSwipe,
    backSwipe,
    calculatePercentProgress,
    resetGameProgress,
    onRestoreGameHistory,
    onUsePrevGameAnswer,
    isAnswerAllLoading,
  };
}
